/**
 * «Share project» in one screen (Figma 826:10305): the search field and the header stay, «People with access» scrolls,
 * «Link access» with «Copy link» and «Apply» stay at the foot. What it lists comes from a `ShareSource`: the catalog in
 * the app's live room, the demo bots in a project of this browser.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { accessCount, type LinkAccess, type SharePerson, type ShareRole, type ShareSource, type ShareState } from '../../collaboration/share/source';
import { applyDraft, EMPTY_DRAFT, hasChanges, reconcile, shownLink, shownRole, stageLink, stageRemove, stageRole, type ShareDraft } from '../../collaboration/share/draft';
import { ActionMenu, type MenuItem } from '../Dropdown';
import { ShareSearchField, ShareSearchResults, useShareSearch } from './ShareSearch';
import { LeaveAlert, RequestsSection, useToast } from './ShareRequests';
import { useAlive } from './useAlive';
import { HomeDialog } from '../home/HomeDialog';
import { Icon, type IconName } from '../Icon';
import { ParticipantAvatar } from './CollaborationOverlay';
import { plural, t, type TranslationKey } from '../../i18n/I18n';

const ROLE: Record<ShareRole, TranslationKey> = { owner: 'Owner', editor: '#CanEdit', viewer: '#CanView' };
const LINK: Record<LinkAccess, { label: TranslationKey; icon: IconName }> = {
  invited: { label: '#OnlyInvitedPeople', icon: 'padlock' }, view: { label: '#AnyoneCanView', icon: 'eye' }, edit: { label: '#AnyoneCanEdit', icon: 'pencil' },
};
const FAILURE = { 'signed-out': '#WebShareSignedOut', gone: '#WebShareAccessGone', failed: '#WebShareLoadFailed' } as const satisfies Record<string, TranslationKey>;
const COPIED_MS = 2000;
const NO_LINK = { member: '#WebShareNoMemberLink', local: '#WebShareLocalNoLink' } as const satisfies Record<string, TranslationKey>;
type SetDraft = (update: (draft: ShareDraft) => ShareDraft) => void;

const keys = new WeakMap<ShareSource, number>();
let lastKey = 0;
function shareKey(source: ShareSource): number {
  if (!keys.has(source)) keys.set(source, ++lastKey);
  return keys.get(source)!;
}

/** What the dialog offers beside the list: the demo's switches, and what follows once you left the project. */
export interface ShareDialogActions {
  onDemoChange?: (on: boolean) => void;
  /** The demo seen as its guest, or back as the owner; unset where there is no such guest. */
  onViewAs?: (guest: boolean) => void;
  /** `name`: yours in the project you left. */
  onLeft?: (name: string) => void;
}

/** The dialog stays and its focus return with it; what is inside starts over, draft and search, when the source changes. */
export function ShareDialog({ source, onClose, ...actions }: { source: ShareSource; onClose: () => void } & ShareDialogActions) {
  return createPortal(<HomeDialog title={t('Share project')} onClose={onClose} closeIcon="closeX" className="share-dialog">
    <ShareContent key={shareKey(source)} source={source} actions={actions} />
  </HomeDialog>, document.body);
}

function ShareContent({ source, actions }: { source: ShareSource; actions: ShareDialogActions }) {
  const state = useSyncExternalStore(source.subscribe, source.get);
  const { toast, say } = useToast();
  const [leaving, setLeaving] = useState(false);
  const { copy, copyError, copyLink } = useCopyLink(source);
  const { draft, setDraft, applying, apply } = useDraft(source, state);
  const input = useRef<HTMLInputElement>(null);
  const search = useShareSearch(source, state, input);
  useEffect(() => { void source.refresh(); }, [source]);
  const owner = state.myRole === 'owner';

  return <>
    <ShareSearchField search={search} input={input} enabled={owner && state.status === 'ready' && state.mode !== 'local' && !applying} />
    <div className="share-scroll">
      {search.status !== 'idle' ? <ShareSearchResults search={search} people={state.people} /> : <>
        <ShareNote state={state} actions={actions} />
        {state.requests.length > 0 && <RequestsSection source={source} requests={state.requests} say={say} />}
        <PeopleSection source={source} state={state} draft={draft} setDraft={setDraft} applying={applying} say={say} onLeave={() => setLeaving(true)} />
      </>}
    </div>
    <div className="share-foot">
      <LinkSection owner={owner} state={state} draft={draft} setDraft={setDraft} applying={applying} />
      {copyError && <p className="share-copy-error" role="alert">{copyError}</p>}
      <footer className="share-actions">
        <button type="button" className="share-copy" aria-busy={copy === 'busy'} aria-disabled={!!state.noLink || undefined}
          data-tip={state.noLink ? t(NO_LINK[state.noLink]) : undefined} onClick={() => { if (!state.noLink) void copyLink(); }}>
          <span className="share-copy-label" data-shown={copy !== 'copied' || undefined}><Icon name="linkChain" size={18} />{t('#CopyLink')}</span>
          <span className="share-copy-label" data-shown={copy === 'copied' || undefined}><Icon name="check" size={18} />{t('Copied')}</span>
        </button>
        {owner && <button type="button" className="share-apply" disabled={!hasChanges(draft) || applying} aria-busy={applying} onClick={() => void apply()}>{t('Apply')}</button>}
      </footer>
    </div>
    <p className="share-toast" role="status" data-shown={toast ? '' : undefined}>{toast}</p>
    {leaving && <LeaveAlert source={source} name={state.people.find(person => person.self)?.name ?? ''} say={say} onLeft={actions.onLeft} onClose={() => setLeaving(false)} />}
  </>;
}

function PeopleSection({ source, state, draft, setDraft, applying, say, onLeave }: {
  source: ShareSource; state: ShareState; draft: ShareDraft; setDraft: SetDraft; applying: boolean; say: (text: string) => void; onLeave: () => void;
}) {
  const ask = () => { void source.requestEdit().catch(() => say(t('#WebEditRequestFailed'))); };
  const menuOf = (person: SharePerson): MenuItem[] | null => person.manageable ? roleMenu(person, draft, state, setDraft)
    : person.self && state.myRole !== 'owner' && state.status === 'ready' ? ownMenu(person, state, ask, onLeave) : null;
  const visitors = plural('#WebShareVisitors', accessCount(state));
  const body = state.status === 'loading' && !state.people.length
    ? <ul className="share-people" aria-hidden>{[0, 1, 2].map(index => <li key={index} className="share-person is-skeleton"><i /><b /></li>)}</ul>
    : state.status === 'error'
      ? <div className="share-failure" role="alert"><p>{t(FAILURE[state.error ?? 'failed'])}</p>
        {state.error !== 'gone' && <button type="button" className="share-text-button" onClick={() => void source.refresh()}>{t('Retry')}</button>}</div>
      : <ul className="share-people">{state.people.map(person => <PersonRow key={person.id} person={person} draft={draft} applying={applying}
        menu={menuOf(person)} />)}</ul>;
  return <section className="share-section" aria-labelledby="share-people-title" aria-busy={state.status === 'loading'}>
    <div className="share-section-head">
      <h3 id="share-people-title">{t('#UserPermissions')}</h3>
      {state.status === 'ready' && <span className="share-count" role="img" aria-label={visitors} data-tip={visitors}><Icon name="multiplayer" size={17} />{visitors}</span>}
    </div>
    {body}
  </section>;
}

function LinkSection({ owner, state, draft, setDraft, applying }: { owner: boolean; state: ShareState; draft: ShareDraft; setDraft: SetDraft; applying: boolean }) {
  const shown = shownLink(draft, state), link = LINK[shown];
  const face = <><span className="share-link-icon"><Icon name={link.icon} size={20} /></span><span>{t(link.label)}</span></>;
  return <section className="share-link" aria-labelledby="share-link-title">
    <h3 id="share-link-title">{t('#AccessSettings')}</h3>
    {owner ? <ActionMenu label={t('#AccessSettings')} width={340} triggerClassName={'share-link-row' + (draft.failed.has('link') ? ' is-failed' : '')} disabled={applying}
      trigger={<>{face}<Icon name="chevronDown" size={16} /></>}
      items={(Object.keys(LINK) as LinkAccess[]).map((mode): MenuItem => ({ id: mode, label: t(LINK[mode].label), checked: shown === mode,
        onSelect: () => setDraft(current => stageLink(current, state, mode)) }))} />
      : <div className="share-link-row">{face}</div>}
    {draft.failed.has('link') && <p className="share-row-error" role="alert">{t('#WebShareChangeFailed')}</p>}
  </section>;
}

/** «Copy link»: «Copied» for two seconds, the reason when the link cannot be had; nothing set after the dialog is gone. */
function useCopyLink(source: ShareSource) {
  const [copy, setCopy] = useState<'idle' | 'busy' | 'copied'>('idle');
  const [copyError, setCopyError] = useState('');
  const alive = useAlive();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copyLink = async () => {
    if (copy === 'busy') return;
    clearTimeout(timer.current);
    setCopy('busy'); setCopyError('');
    let link = '';
    try {
      link = await source.linkUrl();
      if (!alive.current) return;
      await navigator.clipboard.writeText(link);
      if (!alive.current) return;
      setCopy('copied');
      timer.current = setTimeout(() => setCopy('idle'), COPIED_MS);
    } catch {
      if (!alive.current) return;
      setCopy('idle');
      setCopyError(link ? t('#ShareCopyFailed', link) : t('#WebShareLinkFailed'));
    }
  };
  return { copy, copyError, copyLink };
}

function ShareNote({ state, actions: { onDemoChange, onViewAs } }: { state: ShareState; actions: ShareDialogActions }) {
  if (state.mode === 'catalog') return null;
  const demo = state.mode === 'demo';
  if (demo && state.myRole !== 'owner') {
    const me = state.people.find(person => person.self)?.name ?? '';
    return <div className="share-note" role="note"><Icon name="eye" size={18} /><p>{t('#WebShareGuestDemoNotice', me)}</p>
      {onViewAs && <button type="button" onClick={() => onViewAs(false)}>{t('#WebShareViewAsOwner')}</button>}</div>;
  }
  return <div className="share-note" role="note"><Icon name={demo ? 'aiStar' : 'info'} size={18} /><p>{t(demo ? '#ShareDemoNotice' : '#ShareUnavailable')}</p>
    {demo && onViewAs && <button type="button" onClick={() => onViewAs(true)}>{t('#WebShareViewAsGuest')}</button>}
    {onDemoChange && <button type="button" onClick={() => onDemoChange(!demo)}>{t(demo ? '#ShareRemoveBots' : '#ShareShowDemo')}</button>}</div>;
}

/** The staged changes and «Apply»: a fresh list reconciles them; what failed stays staged and marked. */
function useDraft(source: ShareSource, state: ShareState) {
  const [draft, setDraft] = useState<ShareDraft>(EMPTY_DRAFT);
  const [applying, setApplying] = useState(false);
  const alive = useAlive();
  useEffect(() => { if (!applying) setDraft(current => reconcile(current, state)); }, [state, applying]);
  const apply = async () => {
    if (applying || !hasChanges(draft)) return;
    setApplying(true);
    const left = await applyDraft(draft, source);
    if (!alive.current) return;
    setDraft(left);
    setApplying(false);
  };
  return { draft, setDraft, applying, apply };
}

function roleMenu(person: SharePerson, draft: ShareDraft, state: ShareState, setDraft: SetDraft): MenuItem[] {
  const removed = draft.removed.has(person.id), role = shownRole(draft, person);
  const choices: MenuItem[] = person.fixedRole ? [] : [...(['editor', 'viewer'] as const).map((choice): MenuItem => ({ id: choice, label: t(ROLE[choice]),
    checked: !removed && role === choice, onSelect: () => setDraft(current => stageRole(current, state, person.id, choice)) })), { id: 'sep', separator: true }];
  return [...choices, { id: 'remove', label: t('#RemoveAccess'), danger: true, checked: removed, onSelect: () => setDraft(current => stageRemove(current, person.id)) }];
}

/** Your own row as someone other than the owner, as the app's `SharingUserMenuItems` builds it: your role, then «Leave project»; the demo's guest can also ask to edit. */
function ownMenu(person: SharePerson, state: ShareState, ask: () => void, leave: () => void): MenuItem[] {
  const request: MenuItem[] = state.editRequest === 'idle' ? [{ id: 'ask', label: t('#WebAskToEdit'), onSelect: ask }]
    : state.editRequest === 'sent' ? [{ id: 'sent', label: t('#WebEditRequestSent'), disabled: true }] : [];
  return [{ id: 'role', label: t(ROLE[person.role]), checked: true, disabled: true }, ...request, { id: 'sep', separator: true },
    { id: 'leave', label: t('#LeaveProject'), danger: true, onSelect: leave }];
}

function PersonRow({ person, draft, menu, applying }: { person: SharePerson; draft: ShareDraft; menu: MenuItem[] | null; applying: boolean }) {
  const invitation = person.pending && !!person.email, removed = draft.removed.has(person.id), role = shownRole(draft, person);
  const staged = removed || role !== person.role, label = removed ? t('#RemoveAccess') : t(ROLE[role]);
  return <li className={'share-person' + (person.online ? ' is-online' : '') + (removed ? ' is-removed' : '')}>
    {invitation
      ? <span className="share-invite-avatar" aria-hidden>{(person.email ?? '').slice(0, 1).toLocaleUpperCase()}</span>
      : <span className="share-avatar"><ParticipantAvatar name={person.name} look={person.look} size={36} /></span>}
    <span className="share-person-name">
      <span title={person.email ?? person.name}>{invitation ? person.email : person.name}</span>
      {person.self && <small>{t('#ShareYouMark')}</small>}
      {draft.failed.has(person.id) && <em className="share-row-error" role="alert">{t('#WebShareChangeFailed')}</em>}
    </span>
    {person.pending && <span className="share-badge">{t('Pending')}</span>}
    {menu
      ? <ActionMenu label={t('#WebShareAccessOf', person.name)} width={220} align="end" items={menu} disabled={applying} triggerClassName={'share-role' + (staged ? ' is-staged' : '')}
        trigger={<><span>{label}</span><Icon name="chevronDown" size={16} /></>} />
      : <span className="share-role">{label}</span>}
  </li>;
}
